import Axios from 'axios';
import qs from 'qs';
import { StatusValue } from './jobApi';

export interface JobsByCoordinator {
  firstName: string;
  lastName: string;
  userId: string;
  [StatusValue.NOT_STARTED]: number;
  [StatusValue.IN_PROGRESS]: number;
  [StatusValue.ON_HOLD]: number;
  [StatusValue.APPROVAL_PENDING]: number;
  [StatusValue.TO_BE_INVOICED]: number;
  [StatusValue.COMPLETED]: number;
  [StatusValue.CANCELLED]: number;
  [StatusValue.CUSTOMER_CONTACTED]: number;
  [StatusValue.INITIAL_REPORT_SUBMITTED]: number;
  [StatusValue.PROGRESS_REPORT_SUBMITTED]: number;
  [StatusValue.INVOICED]: number;
}

/**
 * One coordinator's job health breakdown. The API returns activity-bucket
 * keys (`H00_24` … `H120_PLUS`); `fetchJobHealthByCoordinator` maps them to
 * these friendlier names so the chart reads naturally. Bucket keys match
 * `ACTIVITY_BUCKETS` so the chart and the jobs table filter agree by
 * construction.
 */
export interface CoordinatorJobHealth {
  userId: string;
  firstName: string;
  lastName: string;
  active: number;
  slowingDown: number;
  stalled: number;
  criticallyStalled: number;
  /**
   * Jobs with no activity record at all — outside every bucket but included
   * in `total`. The API deliberately separates them, so the four visible bar
   * segments can sum to less than `total`.
   */
  noActivity: number;
  total: number;
}

export interface CoordinatorJobHealthResponse {
  coordinators: CoordinatorJobHealth[];
}

/** Raw job-health row as the API returns it. */
interface CoordinatorJobHealthApiRow {
  userId: string;
  firstName: string;
  lastName: string;
  H00_24: number;
  H24_48: number;
  H48_120: number;
  H120_PLUS: number;
  noActivity: number;
  total: number;
}

/**
 * A job ranked by stall time for the "Needs attention" queue. The API returns
 * `id` and `lastActivityAt`; `jobId` and `hoursSinceActive` (what the ranking
 * and the card's duration display are built on) are derived in
 * `fetchStalledJobs`.
 */
export interface StalledJob {
  jobId: string;
  jobNo: string;
  clientName: string | null;
  coordinatorName: string | null;
  currentStatus: StatusValue;
  hoursSinceActive: number;
  lastActivityAt: string | null;
}

export interface StalledJobsResponse {
  jobs: StalledJob[];
  total: number;
  identifier: string;
}

/** Raw stalled-job row as the API returns it. */
interface StalledJobApiRow {
  id: string;
  jobNo: number | null;
  siteAddress: string;
  clientName: string | null;
  coordinatorName: string | null;
  currentStatus: StatusValue;
  lastActivityAt: string | null;
  bucket: string;
}

export async function fetchOverview(): Promise<any> {
  const res = await Axios.get('/api/v1/overview/dashboard-data');
  return res.data;
}

export async function fetchJobTypesClient(clientId: string): Promise<any> {
  const res = await Axios.get(
    `/api/v1/overview/dashboard-data?clientId=${clientId}`
  );

  return res.data;
}

export async function fetchJobStatusByPeriod(
  clientId?: string,
  coordinatorId?: string,
  startDate?: string,
  endDate?: string
) {
  const params = new URLSearchParams();
  if (clientId) params.append('clientId', clientId);
  if (coordinatorId) params.append('coordinatorId', coordinatorId);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const res = await Axios.get(
    `/api/v1/overview/job-status-by-period?${params.toString()}`
  );

  const jobArray: { id: string; value: string }[] = res.data ?? [];
  return jobArray.map((job) => ({ id: job.id, value: parseInt(job.value) }));
}

export interface ClientJobVolume {
  clients: { id: string; companyName: string; jobs: number }[];
  clientCount: number;
}

export async function fetchClientJobVolume(
  startDate?: string,
  endDate?: string,
  coordinatorId?: string
): Promise<ClientJobVolume> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (coordinatorId) params.append('coordinatorId', coordinatorId);

  const res = await Axios.get(
    `/api/v1/overview/client-job-volume?${params.toString()}`
  );

  return res.data as ClientJobVolume;
}

export async function fetchJobCountByCoordinator(
  startDate?: string,
  endDate?: string
) {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const res = await Axios.get(
    `/api/v1/overview/coordinator-workload?${params.toString()}`
  );

  return res.data as JobsByCoordinator[];
}

export interface ClientJobStatus {
  clientId: string;
  companyName: string;
  [StatusValue.NOT_STARTED]: number;
  [StatusValue.IN_PROGRESS]: number;
  [StatusValue.ON_HOLD]: number;
  [StatusValue.APPROVAL_PENDING]: number;
  [StatusValue.TO_BE_INVOICED]: number;
  [StatusValue.COMPLETED]: number;
  [StatusValue.CANCELLED]: number;
  [StatusValue.CUSTOMER_CONTACTED]: number;
  [StatusValue.INITIAL_REPORT_SUBMITTED]: number;
  [StatusValue.PROGRESS_REPORT_SUBMITTED]: number;
  [StatusValue.INVOICED]: number;
}

/**
 * Job status breakdown by client (chart 8). `startDate`/`endDate` filter by
 * `createdOn` when both are given; omit both for all-time. `coordinatorId`
 * narrows to one PM's or supervisor's jobs, same as the other overview charts.
 */
export async function fetchJobStatusByClient(
  startDate?: string,
  endDate?: string,
  coordinatorId?: string
): Promise<ClientJobStatus[]> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (coordinatorId) params.append('coordinatorId', coordinatorId);

  const res = await Axios.get(
    `/api/v1/overview/client-job-status?${params.toString()}`
  );

  return (res.data ?? []) as ClientJobStatus[];
}

/**
 * Job health by coordinator (chart 6). One endpoint per card, as with
 * `client-job-volume` — do not extend `dashboard-data`, which already runs six
 * aggregates in parallel. See `docs/plans/kpi-and-job-health-dashboards.md`.
 *
 * The endpoint takes no period parameter — the activity buckets are
 * inherently relative to "now", so a date range would not change the
 * classification. It returns a bare array with bucket keys, mapped here to
 * the friendlier `CoordinatorJobHealth` shape.
 */
export async function fetchJobHealthByCoordinator(): Promise<CoordinatorJobHealthResponse> {
  const res = await Axios.get('/api/v1/overview/job-health');
  const rows = (res.data ?? []) as CoordinatorJobHealthApiRow[];
  return {
    coordinators: rows.map((row) => ({
      userId: row.userId,
      firstName: row.firstName,
      lastName: row.lastName,
      active: row.H00_24,
      slowingDown: row.H24_48,
      stalled: row.H48_120,
      criticallyStalled: row.H120_PLUS,
      noActivity: row.noActivity,
      total: row.total,
    })),
  };
}

const hoursSince = (iso: string | null): number => {
  if (!iso) return 0;
  const elapsedMs = Date.now() - new Date(iso).getTime();
  return elapsedMs > 0 ? elapsedMs / 3_600_000 : 0;
};

/**
 * Needs attention (chart 7): the most stalled jobs, ranked by
 * `hoursSinceActive`. `limit` caps the list — the card hands off to the jobs
 * table (pre-filtered by `timeSinceLastActive`) for the full view rather than
 * growing its own list. The API defaults to the two stalled buckets
 * (H48_120, H120_PLUS), which is exactly the queue the card shows; the
 * duration is derived client-side from `lastActivityAt`.
 */
export async function fetchStalledJobs(
  limit: number,
  coordinatorId?: string
): Promise<StalledJobsResponse> {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (coordinatorId) params.append('coordinatorId', coordinatorId);
  const res = await Axios.get(
    `/api/v1/overview/stalled-jobs?${params.toString()}`
  );
  const rows = (res.data?.jobs ?? []) as StalledJobApiRow[];
  return {
    jobs: rows.map((job) => ({
      jobId: job.id,
      jobNo: job.jobNo != null ? String(job.jobNo) : '',
      clientName: job.clientName,
      coordinatorName: job.coordinatorName,
      currentStatus: job.currentStatus,
      hoursSinceActive: hoursSince(job.lastActivityAt),
      lastActivityAt: job.lastActivityAt,
    })),
    total: res.data?.total ?? rows.length,
    identifier: res.data?.identifier ?? '',
  };
}

/**
 * Dimension to group the KPI summary by. Re-keys all three KPI charts so a
 * single control drives the whole tab. Values match the API's `KpiGroupBy`
 * enum.
 */
export type KpiGroupBy = 'client' | 'state' | 'supervisor' | 'coordinator';

/** The five KPI stages, as keyed in the API's `stages` map. */
export type KpiStageName =
  | 'creationToContact'
  | 'contactToAttendance'
  | 'attendanceToReport'
  | 'reportToCompletion'
  | 'completionToInvoice';

/**
 * Mean/median duration for one stage plus the counts behind them — `n` is
 * the job count the average is over (so one 40-day outlier is read in
 * context) and `excluded` counts out-of-order checkpoints the averages skip.
 */
export interface KpiStageStats {
  /** Mean duration in hours; null when no job had both checkpoints. */
  avgHours: number | null;
  medianHours: number | null;
  n: number;
  excluded: number;
}

/** Aggregate row for one group — or the whole org, via `overall`. */
export interface KpiSummaryRow {
  jobCount: number;
  stages: Record<KpiStageName, KpiStageStats>;
  /** Conversion counts across the five checkpoints. */
  funnel: {
    created: number;
    contacted: number;
    attended: number;
    reported: number;
    completed: number;
  };
}

export type KpiGroup = KpiSummaryRow & {
  key: string | null;
  label: string;
};

export interface KpiSummaryResponse {
  groups: KpiGroup[];
  overall: KpiSummaryRow;
}

/**
 * KPI summary (charts 1–3). Lives under `management-reports` (not `overview`)
 * because it reuses the job report's filter parsing: it accepts the same
 * `JobReportFilter` query params as the xlsx export, so the charts and the
 * spreadsheet see the same job set. Serializes exactly like
 * `generateJobReport` — repeated arrays, bracketed date ranges — which the
 * API's query parsing is built around.
 */
export async function fetchKpiSummary(
  groupBy: KpiGroupBy,
  filters: Record<string, unknown>
): Promise<KpiSummaryResponse> {
  const res = await Axios.get('/api/v1/management-reports/kpi-summary', {
    params: { groupBy, ...filters },
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
  });
  return res.data as KpiSummaryResponse;
}
